import React from "react";
import styles from "./CygnusForm.module.css";
import { motion } from "framer-motion";
import { SectionLayout } from "../InputFieldsController/InputFieldsController.utils";

interface SectionWrapperProps {
  sectionName?: string;
  sectionLayout?: SectionLayout;
  sectionIndex?: number;
  customStyles?: {
    backgroundColor?: string;
    borderColor?: string;
    titleColor?: string;
  };
  children: React.ReactNode;
}

const SectionWrapper: React.FC<SectionWrapperProps> = ({
  sectionName,
  sectionLayout = "plain",
  sectionIndex,
  customStyles = {},
  children,
}) => {
  const { backgroundColor, borderColor, titleColor } = customStyles;

  const motionProps = {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.3 },
  };

  if (sectionLayout === "banner") {
    return (
      <motion.div
        className={styles.sectionWrapper}
        data-layout="banner"
        {...motionProps}
        style={{ borderColor }}
      >
        {sectionName && (
          <div className={styles.sectionBannerHeader} style={{ backgroundColor, color: titleColor }}>
            {sectionName}
          </div>
        )}
        <div className={styles.sectionBannerBody}>{children}</div>
      </motion.div>
    );
  }

  if (sectionLayout === "accent") {
    return (
      <motion.div
        className={`${styles.sectionWrapper} ${styles.sectionAccent}`}
        data-layout="accent"
        {...motionProps}
        style={{ borderLeftColor: borderColor, backgroundColor }}
      >
        {sectionName && (
          <div
            className={styles.sectionAccentHeader}
            style={{ background: `linear-gradient(to right, ${borderColor ?? "#BFDBFE"}30, transparent)` }}
          >
            <span className={styles.sectionAccentDot} style={{ backgroundColor: borderColor }} />
            <span className={styles.sectionAccentTitle} style={{ color: titleColor }}>
              {sectionName}
            </span>
          </div>
        )}
        <div className={styles.sectionBody}>{children}</div>
      </motion.div>
    );
  }

  if (sectionLayout === "stepped") {
    return (
      <motion.div
        className={`${styles.sectionWrapper} ${styles.sectionStepped}`}
        data-layout="stepped"
        {...motionProps}
        style={{ backgroundColor, borderColor: borderColor ? `1px solid ${borderColor}` : undefined }}
      >
        <div className={styles.sectionSteppedHeader}>
          <div className={styles.sectionStepBadge} style={{ backgroundColor: borderColor ?? titleColor, color: "#fff" }}>
            {sectionIndex ?? ""}
          </div>
          {sectionName && (
            <span className={styles.sectionStepTitle} style={{ color: titleColor }}>
              {sectionName}
            </span>
          )}
        </div>
        <div className={styles.sectionBody}>{children}</div>
      </motion.div>
    );
  }

  if (sectionLayout === "gradient") {
    return (
      <motion.div
        className={styles.sectionWrapper}
        data-layout="gradient"
        {...motionProps}
        style={{ borderColor }}
      >
        {sectionName && (
          <div
            className={styles.sectionGradientHeader}
            style={{
              background: `linear-gradient(135deg, ${titleColor ?? "#1E3A8A"} 0%, ${borderColor ?? "#BFDBFE"} 100%)`,
            }}
          >
            {sectionName}
          </div>
        )}
        <div className={styles.sectionGradientBody} style={{ backgroundColor }}>{children}</div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className={styles.sectionWrapper}
      data-layout="plain"
      {...motionProps}
      style={{ backgroundColor, borderColor }}
    >
      {sectionName && (
        <div className={styles.sectionHeader} style={{ color: titleColor }}>
          {sectionName}
        </div>
      )}
      <div className={styles.sectionBody}>{children}</div>
    </motion.div>
  );
};

export default SectionWrapper;
